// Iter46-B — Admin → Pending User Approvals.
// Platform admin: list all pending + approve/reject actions.
// Tenant admin: list of users they invited (read-only — only platform_admin
// can approve).
import React, { useState } from "react";
import { UserCheck, UserX, Mail, Building2, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import { usePendingUsers, useApproveUser, useRejectUser, useTenants } from "../api/sitroom";

const ROLE_COLORS = {
  platform_admin: "#E24B4A",
  tenant_admin: "#f3b14a",
  escalation_manager: "#185FA5",
  responder: "#9198b0",
  observer: "#6b7388",
};

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

export default function AdminPendingApprovals() {
  const { user: me } = useAuth();
  const addToast = useStore((s) => s.addToast);
  const { data: pending = [], isLoading } = usePendingUsers();
  const { data: tenants = [] } = useTenants();
  const approveMut = useApproveUser();
  const rejectMut = useRejectUser();

  const tenantName = (id) => tenants.find((t) => t.id === id)?.name || id || "—";
  const isPlatform = me?.role === "platform_admin";

  const [reasonMap, setReasonMap] = useState({});

  const approve = async (u) => {
    try {
      await approveMut.mutateAsync({ id: u.id, reason: reasonMap[u.id] || null });
      addToast({ type: "success", title: "User approved", body: `${u.email} is now active` });
      setReasonMap((m) => ({ ...m, [u.id]: "" }));
    } catch (e) {
      addToast({ type: "error", title: "Approve failed", body: e?.response?.data?.detail || e.message });
    }
  };
  const reject = async (u) => {
    if (!window.confirm(`Reject invitation for ${u.email}?`)) return;
    try {
      await rejectMut.mutateAsync({ id: u.id, reason: reasonMap[u.id] || null });
      addToast({ type: "info", title: "User rejected", body: u.email });
    } catch (e) {
      addToast({ type: "error", title: "Reject failed", body: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="admin-pending-approvals">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">
          Pending user approvals · {pending.length}
        </div>
        {isPlatform && (
          <div className="px-2 py-1 rounded inline-flex items-center gap-1.5"
            style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.35)" }}>
            <Shield size={11} color="#f3b14a" />
            <span style={mono(9, "#f3b14a", 700, "0.4px")} className="uppercase">Platform gatekeeper</span>
          </div>
        )}
      </div>

      {isLoading && (
        <div style={mono(11, "var(--dimmer, #9198b0)")} className="py-4">Loading pending invitations…</div>
      )}

      {!isLoading && pending.length === 0 && (
        <div className="rounded-md p-6" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px dashed rgba(255,255,255,0.15)" }} data-testid="pending-empty">
          <div style={mono(12, "var(--text-dim, #c5cad8)")}>
            No pending invitations. {isPlatform ? "Tenant admins can invite users via /admin/users." : "Invite users from /admin/users — they will appear here for platform admin approval."}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {pending.map((u) => (
          <div
            key={u.id}
            className="rounded-md p-3"
            style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(243,177,74,0.30)" }}
            data-testid={`pending-row-${u.id}`}
          >
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Mail size={13} color="#f3b14a" />
                <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
                  {u.name || u.email}
                </span>
                <span style={mono(11, "#9ec3ec")}>{u.email}</span>
                <span className="px-1.5 py-0.5 rounded"
                  style={{ background: `${ROLE_COLORS[u.role] || "#9198b0"}1f`, color: ROLE_COLORS[u.role] || "#9198b0", ...mono(9, undefined, 700, "0.4px"), textTransform: "uppercase" }}>
                  {u.role}
                </span>
              </div>
              <div className="px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                style={{ background: "rgba(243,177,74,0.18)", border: "1px solid rgba(243,177,74,0.45)" }}>
                <span style={mono(9, "#f3b14a", 700, "0.4px")} className="uppercase">pending approval</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mb-2" style={mono(10, "var(--dimmer, #9198b0)")}>
              {u.tenant_id && (
                <span className="inline-flex items-center gap-1">
                  <Building2 size={10} /> {tenantName(u.tenant_id)}
                </span>
              )}
              {u.created_by && <span>Invited by <b style={{ color: "var(--text-dim, #c5cad8)" }}>{u.created_by}</b></span>}
              {u.invited_by_role && <span>({u.invited_by_role})</span>}
              {u.created_at && <span>{new Date(u.created_at).toLocaleString()}</span>}
            </div>
            {isPlatform && (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-center mt-2">
                <input
                  type="text"
                  placeholder="Optional reason / note (audit log)"
                  value={reasonMap[u.id] || ""}
                  onChange={(e) => setReasonMap((m) => ({ ...m, [u.id]: e.target.value }))}
                  style={{
                    background: "var(--bg-base, #08090d)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    color: "var(--text, #f0f2f8)",
                    ...mono(11),
                    padding: "6px 10px",
                    borderRadius: 6,
                    outline: "none",
                  }}
                  data-testid={`pending-reason-${u.id}`}
                />
                <button
                  type="button"
                  onClick={() => approve(u)}
                  disabled={approveMut.isPending}
                  className="px-3 py-1.5 rounded inline-flex items-center gap-1"
                  style={{
                    background: "rgba(78,208,132,0.15)",
                    border: "1px solid rgba(78,208,132,0.55)",
                    color: "#4ed084",
                    ...mono(10, undefined, 700, "0.4px"),
                    cursor: "pointer",
                  }}
                  data-testid={`pending-approve-${u.id}`}
                >
                  <UserCheck size={12} /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => reject(u)}
                  disabled={rejectMut.isPending}
                  className="px-3 py-1.5 rounded inline-flex items-center gap-1"
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(226,75,74,0.45)",
                    color: "#E24B4A",
                    ...mono(10, undefined, 700, "0.4px"),
                    cursor: "pointer",
                  }}
                  data-testid={`pending-reject-${u.id}`}
                >
                  <UserX size={12} /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
